import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import type { z } from 'zod';
import { apiErrorSchema } from './schemas/common';

export interface ApiClientConfig {
  /** Base URL for API routes. Defaults to '/api' (dev-proxied to Laravel). */
  baseURL?: string;
  /**
   * Origin that serves /sanctum/csrf-cookie. Defaults to '' so the request
   * goes to the same origin and is picked up by the Vite proxy.
   */
  sanctumBaseURL?: string;
  /** Where to send the browser when re-auth fails. */
  loginPath?: string;
  onUnauthorized?: () => void;
}

export class ApiError extends Error {
  readonly status: number;
  readonly errors: Record<string, string[]>;

  constructor(status: number, message: string, errors: Record<string, string[]> = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errors = errors;
  }

  /** First message for a field, for wiring into React Hook Form. */
  fieldError(field: string): string | undefined {
    return this.errors[field]?.[0];
  }
}

const CSRF_COOKIE = 'XSRF-TOKEN';
const CSRF_HEADER = 'X-XSRF-TOKEN';
const MUTATING = new Set(['post', 'put', 'patch', 'delete']);

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retriedAfterRefresh?: boolean;
  _retriedAfterCsrf?: boolean;
}

export function createApiClient(config: ApiClientConfig = {}) {
  const {
    baseURL = '/api',
    sanctumBaseURL = '',
    loginPath = '/login',
    onUnauthorized,
  } = config;

  const http: AxiosInstance = axios.create({
    baseURL,
    // Sanctum SPA cookie mode: auth rides in httpOnly SameSite=Strict cookies
    // (§10). Nothing is ever put in localStorage.
    withCredentials: true,
    withXSRFToken: true,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });

  /**
   * Because credentials travel in cookies the browser attaches automatically,
   * CSRF protection is REQUIRED — it is not redundant with the token auth.
   * Do not remove this layer.
   */
  let csrfPromise: Promise<void> | null = null;

  async function ensureCsrfCookie(force = false): Promise<void> {
    if (!force && readCookie(CSRF_COOKIE)) return;
    if (!csrfPromise || force) {
      csrfPromise = axios
        .get(`${sanctumBaseURL}/sanctum/csrf-cookie`, {
          withCredentials: true,
          headers: { Accept: 'application/json' },
        })
        .then(() => undefined)
        .finally(() => {
          csrfPromise = null;
        });
    }
    await csrfPromise;
  }

  http.interceptors.request.use(async (req: InternalAxiosRequestConfig) => {
    const method = (req.method ?? 'get').toLowerCase();
    if (MUTATING.has(method)) {
      await ensureCsrfCookie();
      const token = readCookie(CSRF_COOKIE);
      if (token) req.headers.set(CSRF_HEADER, token);
    }
    return req;
  });

  /** Single-flight refresh so a burst of 401s triggers exactly one rotation. */
  let refreshPromise: Promise<void> | null = null;

  async function refreshSession(): Promise<void> {
    if (!refreshPromise) {
      refreshPromise = axios
        .post(
          `${baseURL}/auth/refresh`,
          {},
          {
            withCredentials: true,
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
              ...(readCookie(CSRF_COOKIE) ? { [CSRF_HEADER]: readCookie(CSRF_COOKIE) as string } : {}),
            },
          },
        )
        .then(() => undefined)
        .finally(() => {
          refreshPromise = null;
        });
    }
    await refreshPromise;
  }

  function handleUnauthorized(): void {
    if (onUnauthorized) {
      onUnauthorized();
      return;
    }
    if (typeof window !== 'undefined' && window.location.pathname !== loginPath) {
      window.location.assign(loginPath);
    }
  }

  http.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
      const original = error.config as RetriableConfig | undefined;
      const status = error.response?.status;

      // 419 = Laravel's "CSRF token mismatch" (expired session cookie).
      // Re-mint the cookie and replay once.
      if (status === 419 && original && !original._retriedAfterCsrf) {
        original._retriedAfterCsrf = true;
        await ensureCsrfCookie(true);
        return http.request(original);
      }

      if (status === 401 && original && !original._retriedAfterRefresh) {
        const isAuthCall =
          typeof original.url === 'string' && /\/auth\/(refresh|login|logout)$/.test(original.url);
        if (!isAuthCall) {
          original._retriedAfterRefresh = true;
          try {
            await refreshSession();
            return await http.request(original);
          } catch {
            handleUnauthorized();
          }
        } else if (original.url?.endsWith('/auth/refresh')) {
          handleUnauthorized();
        }
      }

      const body = error.response?.data;
      const parsed = apiErrorSchema.safeParse(body);
      throw new ApiError(
        status ?? 0,
        parsed.success ? parsed.data.message : (error.message ?? 'Request failed'),
        parsed.success ? (parsed.data.errors ?? {}) : {},
      );
    },
  );

  return { http, ensureCsrfCookie, refreshSession };
}

export type ApiClient = ReturnType<typeof createApiClient>;

let instance: ApiClient | null = null;

/** Call once at app bootstrap. */
export function configureApiClient(config: ApiClientConfig = {}): ApiClient {
  instance = createApiClient(config);
  return instance;
}

export function getApiClient(): ApiClient {
  if (!instance) instance = createApiClient();
  return instance;
}

export function http(): AxiosInstance {
  return getApiClient().http;
}

/* ---------------- request helpers used by the endpoint modules --------------- */

export async function get<S extends z.ZodTypeAny>(
  url: string,
  schema: S,
  params?: Record<string, unknown>,
  config?: AxiosRequestConfig,
): Promise<z.infer<S>> {
  const res = await http().get(url, { params, ...config });
  return schema.parse(res.data) as z.infer<S>;
}

export async function post<S extends z.ZodTypeAny>(
  url: string,
  schema: S,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<z.infer<S>> {
  const res = await http().post(url, body, config);
  return schema.parse(res.data) as z.infer<S>;
}

export async function put<S extends z.ZodTypeAny>(
  url: string,
  schema: S,
  body?: unknown,
): Promise<z.infer<S>> {
  const res = await http().put(url, body);
  return schema.parse(res.data) as z.infer<S>;
}

export async function del<S extends z.ZodTypeAny>(url: string, schema: S): Promise<z.infer<S>> {
  const res = await http().delete(url);
  return schema.parse(res.data) as z.infer<S>;
}

/** For endpoints whose response body we don't model. */
export async function command(url: string, body?: unknown): Promise<void> {
  await http().post(url, body);
}
